import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { loadSession, setAuth, useAuth } from './lib/api.js';
import { store } from './lib/store.js';
import { useAccess, useConnection, useHomes, useReady, useUser } from './lib/hooks.js';
import { lastHome } from './lib/prefs.js';
import { nearestHome, isNative } from './lib/native.js';
import { HomeLayout, Shell } from './shell/Shell.js';
import { AccessEndedScreen, ForcedPasswordScreen, LoginScreen, OfflineScreen, RedeemScreen, Splash } from './screens/auth.js';
import { DialogHost, Empty, SkeletonTiles, ToastHost } from './components/ui/index.js';

const HomeScreen = lazy(() => import('./screens/HomeScreen.js'));
const RoomScreen = lazy(() => import('./screens/RoomScreen.js'));
const UnsortedScreen = lazy(() => import('./screens/UnsortedScreen.js'));
const OverviewScreen = lazy(() => import('./screens/OverviewScreen.js'));
const SettingsScreen = lazy(() => import('./screens/SettingsScreen.js'));
// Route slots for the admin / config / dashboard UIs built on top of this shell.
const AdminRoot = lazy(() => import('./screens/admin/index.js'));
const ConfigRoot = lazy(() => import('./screens/config/index.js'));
const DashboardRoot = lazy(() => import('./screens/dashboards/index.js'));
const MusicRoot = lazy(() => import('./screens/music/index.js'));
const CalendarScreen = lazy(() => import('./screens/calendar/index.js'));
const TalkScreen = lazy(() => import('./screens/talk/TalkScreen.js'));
const CameraEventsScreen = lazy(() => import('./screens/camera/events/EventsScreen.js'));
// Wall tablets: full-screen /t/:tabletId (no shell) and the one-time /pair page.
const TabletScreen = lazy(() => import('./screens/tablet/index.js'));
const PairScreen = lazy(() => import('./screens/tablet/PairScreen.js'));

export function App(): ReactNode {
  return (
    <BrowserRouter>
      <Root />
      <ToastHost />
      <DialogHost />
    </BrowserRouter>
  );
}

function Root(): ReactNode {
  const auth = useAuth();
  const loc = useLocation();
  useEffect(() => {
    void loadSession();
  }, []);
  useEffect(() => {
    if (auth.status !== 'offline') return;
    const retry = (): void => void loadSession();
    window.addEventListener('online', retry);
    const t = window.setInterval(retry, 15_000);
    return () => {
      window.removeEventListener('online', retry);
      window.clearInterval(t);
    };
  }, [auth.status]);

  if (auth.status === 'loading') return <Splash />;
  if (auth.status === 'offline') return <OfflineScreen onRetry={() => void loadSession()} />;
  if (auth.status === 'anon') {
    const next = loc.pathname === '/' || loc.pathname === '/login' ? '' : `?next=${encodeURIComponent(loc.pathname + loc.search)}`;
    return (
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/redeem" element={<RedeemScreen />} />
        <Route path="/pair" element={<Suspense fallback={<Splash />}><PairScreen /></Suspense>} />
        {/* A signed-out wall tablet pairs instead of signing in. */}
        <Route path="/t/*" element={<Navigate to="/pair" replace />} />
        <Route path="*" element={<Navigate to={`/login${next}`} replace />} />
      </Routes>
    );
  }
  if (auth.user.mustChangePassword) return <ForcedPasswordScreen />;
  return <Authed userId={auth.user.id} />;
}

function Authed({ userId }: { userId: string }): ReactNode {
  const conn = useConnection();
  useEffect(() => {
    store.connect(userId);
    return () => store.disconnect();
  }, [userId]);
  useEffect(() => {
    if (conn === 'unauthenticated') setAuth({ status: 'anon' });
  }, [conn]);
  // The server sends welcome only (no snapshots) when the password must be
  // changed, e.g. an admin reset it while this tab was open. Route to the
  // change-password screen; the socket reconnects once it's done.
  const wsUser = useUser();
  useEffect(() => {
    if (wsUser?.mustChangePassword) {
      store.disconnect(true);
      setAuth({ status: 'authed', user: wsUser });
    }
  }, [wsUser]);
  if (conn === 'revoked') return <AccessEndedScreen guestName={wsUser?.isGuest ? wsUser.displayName : undefined} />;

  return (
    <Routes>
      <Route path="/login" element={<LoggedInLogin />} />
      <Route path="/redeem" element={<Navigate to="/" replace />} />
      <Route path="/pair" element={<Suspense fallback={<Splash />}><PairScreen /></Suspense>} />
      <Route path="/t/:tabletId/*" element={<Suspense fallback={<Splash />}><TabletScreen /></Suspense>} />
      <Route element={<Shell />}>
        <Route index element={<HomeRedirect />} />
        <Route path="overview" element={<OverviewScreen />} />
        <Route path="settings" element={<SettingsScreen />} />
        <Route path="h/:home" element={<HomeLayout />}>
          <Route index element={<HomeScreen />} />
          <Route path="room/:roomId" element={<RoomScreen />} />
          <Route path="unsorted" element={<UnsortedScreen />} />
          <Route path="admin/*" element={<AdminRoot />} />
          <Route path="settings/*" element={<ConfigRoot />} />
          <Route path="config/*" element={<LegacyConfig />} />
          <Route path="dashboards/:module" element={<DashboardRoot />} />
          <Route path="music/*" element={<MusicRoot />} />
          <Route path="calendar" element={<CalendarScreen />} />
          <Route path="talk/:device" element={<TalkScreen />} />
          <Route path="cameras/events" element={<CameraEventsScreen />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

let nearestTried = false;

/** "/" → nearest home (iOS geofence, once per session) → last-used home → first home. */
function HomeRedirect(): ReactNode {
  const user = useUser();
  // A wall tablet's device account (username tablet-<id>) lives on its tablet page.
  if (user?.username.startsWith('tablet-')) return <Navigate to={`/t/${user.username.slice('tablet-'.length)}`} replace />;
  return <HomeRedirectFor />;
}

function HomeRedirectFor(): ReactNode {
  const homes = useHomes();
  const ready = useReady();
  const [nearest, setNearest] = useState<string | null | undefined>(isNative() && !nearestTried ? undefined : null);
  useEffect(() => {
    if (nearest !== undefined) return;
    nearestTried = true;
    void nearestHome().then(setNearest);
  }, [nearest]);
  if (!ready || nearest === undefined)
    return (
      <div className="shell">
        <div className="appbar-skeleton" />
        <SkeletonTiles count={8} />
      </div>
    );
  if (homes.length === 0)
    return (
      <div className="shell">
        <Empty icon="home" title="No homes yet">
          Your account doesn't have access to any home. Ask an admin to add you.
        </Empty>
      </div>
    );
  const last = lastHome.get();
  const pick = homes.find((h) => h.id === nearest) ?? homes.find((h) => h.id === last) ?? homes[0]!;
  return <HomeOrStay home={pick.id} />;
}

/** Guests with a stay land on "Your stay"; everyone else on the home screen. */
function HomeOrStay({ home }: { home: string }): ReactNode {
  const user = useUser();
  const access = useAccess(home);
  const stay = !!user?.isGuest && !!access?.modules.includes('guests');
  return <Navigate to={stay ? `/h/${home}/dashboards/guests` : `/h/${home}`} replace />;
}

/** Old /h/:home/config/* links: entity pages keep working, everything else lands on Settings. */
function LegacyConfig(): ReactNode {
  const { home = '', '*': rest = '' } = useParams();
  const keep = /^(entity|p)\/.+$/.test(rest);
  return <Navigate to={keep ? `/h/${home}/settings/${rest}` : `/h/${home}/settings`} replace />;
}

/** Already signed in on /login: go to `next`, loading server-rendered pages (iOS install) from the server. */
function LoggedInLogin(): ReactNode {
  const next = new URLSearchParams(useLocation().search).get('next') ?? '/';
  const server = /^\/(?:api\/)?ios(?:\/|$)/.test(next);
  useEffect(() => {
    if (server) window.location.assign(next);
  }, [server, next]);
  return server ? null : <Navigate to={next.startsWith('/') && !next.startsWith('//') ? next : '/'} replace />;
}

/** Server-rendered paths an older cached service worker may still route into the app. */
const SERVER_PAGES = /^\/ios(?:\/|$)/;

function NotFound(): ReactNode {
  // An old service worker answered a server page with the app shell: update it, then load the real page once.
  useEffect(() => {
    if (!SERVER_PAGES.test(location.pathname) || sessionStorage.getItem('sw-bypass') === location.pathname) return;
    sessionStorage.setItem('sw-bypass', location.pathname);
    void (navigator.serviceWorker?.getRegistration() ?? Promise.resolve(undefined))
      .then((r) => r?.unregister())
      .finally(() => location.reload());
  }, []);
  return (
    <div className="shell">
      <Empty icon="question" title="Page not found">
        That link doesn't go anywhere. <a href="/">Go home</a>
      </Empty>
    </div>
  );
}
